"use client";

import { AlertCircle, Loader2 } from "lucide-react";
import type { Route } from "next";
import Link from "next/link";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { routes } from "@/utils/routes";

/**
 * Reusable loading state component
 */
export function LoadingState({
  message = "Loading...",
  className = "mx-auto max-w-6xl py-12 text-center",
}: {
  message?: string;
  className?: string;
}) {
  return (
    <div className={className}>
      <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
      <p className="mt-4 text-muted-foreground">{message}</p>
    </div>
  );
}

/**
 * Reusable error state component
 */
export function ErrorState({
  error,
  title = "Something went wrong",
  actionText = "Try Again",
  actionHref,
  onRetry,
  className = "mx-auto max-w-6xl py-12",
}: {
  error: Error | string;
  title?: string;
  actionText?: string;
  actionHref?: string;
  onRetry?: () => void;
  className?: string;
}) {
  const errorMessage = typeof error === "string" ? error : error.message;

  return (
    <div className={className}>
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          {title}: {errorMessage}
        </AlertDescription>
      </Alert>
      <div className="mt-6 text-center">
        {onRetry ? (
          <Button onClick={onRetry}>{actionText}</Button>
        ) : (
          <Button asChild>
            <Link href={(actionHref || routes.shop) as Route}>
              {actionText}
            </Link>
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Reusable not found state component
 */
export function NotFoundState({
  title = "Not Found",
  description = "The item you're looking for doesn't exist or is no longer available.",
  actionText = "Go Back",
  actionHref = routes.shop,
  className = "mx-auto max-w-6xl py-12 text-center",
}: {
  title?: string;
  description?: string;
  actionText?: string;
  actionHref?: string;
  className?: string;
}) {
  return (
    <div className={className}>
      <h1 className="font-bold font-heading text-2xl">{title}</h1>
      <p className="mt-2 text-muted-foreground">{description}</p>
      <Button asChild className="mt-4">
        <Link href={actionHref as Route}>{actionText}</Link>
      </Button>
    </div>
  );
}

/**
 * Product-specific loading state
 */
export function ProductLoadingState() {
  return <LoadingState message="Loading product..." />;
}

/**
 * Product-specific error state
 */
export function ProductErrorState({ error }: { error: Error }) {
  return (
    <ErrorState
      actionHref={routes.shop}
      actionText="Browse Products"
      error={error}
      title="Failed to load product"
    />
  );
}

/**
 * Product-specific not found state
 * @deprecated Use Product404 component from error-pages instead
 */
export function ProductNotFoundState() {
  return (
    <NotFoundState
      actionHref={routes.shop}
      actionText="Browse Products"
      description="The product you're looking for doesn't exist or is no longer available."
      title="Product not found"
    />
  );
}

/**
 * Generic empty state component
 */
export function EmptyState({
  title = "No items found",
  description = "There are no items to display at the moment.",
  actionText,
  actionHref,
  icon: Icon,
  className = "mx-auto max-w-6xl py-12 text-center",
}: {
  title?: string;
  description?: string;
  actionText?: string;
  actionHref?: string;
  icon?: React.ComponentType<{ className?: string }>;
  className?: string;
}) {
  return (
    <div className={className}>
      {Icon && (
        <Icon className="mx-auto mb-4 h-12 w-12 text-muted-foreground/50" />
      )}
      <h2 className="font-bold font-heading text-lg">{title}</h2>
      <p className="mt-2 text-muted-foreground">{description}</p>
      {actionText && actionHref && (
        <Button asChild className="mt-4">
          <Link href={actionHref as Route}>{actionText}</Link>
        </Button>
      )}
    </div>
  );
}
